import { ldClient } from "./client";
import {
  ensureLdConnected,
  getSelectedLdShopItem,
  handleLdServerEvent,
  hideLdHostPanel,
  hideLdLeaderboardPanel,
  hideLdPlayerPanel,
  isLootDropActive,
  releaseLootDropPointer,
  setSelectedLdShopItem,
  showLdHostPanel,
  showLdLeaderboardPanel,
  showLdPlayerPanel,
  startLootDropSession,
  stopLootDropSession,
  syncLootDropRemotesFromRoom,
  updateLdHostPlayersList,
  updateLdLeaderboard,
  updateLdPlayerPanelBalance,
  updateLdPlayerPanelHostBalance,
  updateLdSessionLimitUi,
} from "./game-runtime";
import { ldIsRemoteWsUrl, ldWsUrl, LD_RAIN_MAX_BALLS } from "../../../lib/multiplayer/loot-drop-protocol";
import { hideHubScreens } from "../game-hub";
import { withExitPlayTransition } from "../play-transition";
import { syncToggleSwitch } from "../toggle-switch";
import type { LdRoomListEntry } from "../../../lib/multiplayer/loot-drop-protocol";
import {
  addOwnedCosmetic,
  getDotsBalance,
  getDotsState,
  adjustDotsBalance,
  applyDotsFromApi,
} from "../dots-currency";
import {
  fetchShopCatalog,
  postLootDropDotsSync,
  postLootDropGrantCosmetic,
  type ShopCosmeticDto,
} from "../../lib/cloud";
import { loadCosmeticImage, filterPlayCosmeticIds } from "../cursor-cosmetics";

export type LdUiHooks = {
  showEl: (el: HTMLElement | null) => void;
  hideEl: (el: HTMLElement | null) => void;
  openGameHub: () => void;
  playScreenEnterMotion: () => void;
  playSelectSound: () => void;
  playModeCardBeep: () => void;
  getPlayerName: () => string;
  getAccountUserId?: () => string | null;
  getCursorColor?: () => string;
  fetchFriends?: () => Promise<Array<{ id: string; displayName?: string | null }>>;
  stopPlaying: () => void;
  isPlaying: () => boolean;
  releaseGamePointer?: () => void;
  updateMusicState?: () => void;
  refreshDotsUi?: () => void;
};

const GUEST_NAME_KEY = "ldGuestName";

function resolveEquippedCosmeticIds(): string[] {
  return filterPlayCosmeticIds(getDotsState().equippedCosmeticIds);
}

function resolveCursorColor(): string {
  return hooks?.getCursorColor?.() || "#ffffff";
}

function ldConnectionErrorMessage(err?: unknown): string {
  const url = ldClient.lastConnectUrl ?? ldWsUrl();
  const host = location.hostname;
  const code = err instanceof Error ? err.message : "";

  if (code === "connection_timeout" && ldIsRemoteWsUrl(url)) {
    return `Server did not respond in time (${url}). On Render free tier the first connection can take up to 60s — try again.`;
  }
  if (code === "connection_timeout") {
    return "Connection timed out. Stop old terminals and run: npm run dev";
  }
  if (host === "localhost" || host === "127.0.0.1") {
    return "LootDrop server not running. Stop any old dev server and run: npm run dev";
  }
  return `Could not connect to ${url}. Check the WebSocket service is running.`;
}

let hooks: LdUiHooks | null = null;
let onlyFriendsToggle = false;
let rainDotsUiOpen = false;
let hostInProgress = false;
let listRefreshTimer: ReturnType<typeof setInterval> | null = null;
let shopSelectCatalog: ShopCosmeticDto[] = [];
/** Serializes /api/dots lootdrop persists so concurrent spends cannot race. */
let ldDotsPersistChain: Promise<void> = Promise.resolve();
/** Last room-ledger balance for the local player (WS source of truth during the session). */
let ldLastRoomDotsBalance: number | null = null;

const ldListScreen = () => document.getElementById("ldListScreen");
const ldHostScreen = () => document.getElementById("ldHostScreen");
const ldShopSelectScreen = () => document.getElementById("ldShopSelectScreen");
const ldListError = () => document.getElementById("ldListError");
const ldHostError = () => document.getElementById("ldHostError");
const ldRoomsList = () => document.getElementById("ldRoomsList");
const ldHostBalance = () => document.getElementById("ldHostBalance");
const ldMaxCursorsInput = () => document.getElementById("ldMaxCursorsInput") as HTMLInputElement | null;
const ldSessionDotsLimitInput = () =>
  document.getElementById("ldSessionDotsLimitInput") as HTMLInputElement | null;
const ldOnlyFriendsBtn = () => document.getElementById("ldOnlyFriendsBtn") as HTMLButtonElement | null;
const ldOnlyFriendsHint = () => document.getElementById("ldOnlyFriendsHint");

export function initLootDropUi(h: LdUiHooks): void {
  hooks = h;

  document.getElementById("ldHostLootDropBtn")?.addEventListener("click", () => showHostScreen());
  document.getElementById("ldListBackBtn")?.addEventListener("click", () => leaveToHub());
  document.getElementById("ldHostBackBtn")?.addEventListener("click", () => showListScreen());
  document.getElementById("ldHostStartBtn")?.addEventListener("click", () => void submitHost());
  document.getElementById("ldOnlyFriendsBtn")?.addEventListener("click", () => toggleOnlyFriends());
  document.getElementById("ldEndLootDropBtn")?.addEventListener("click", () => {
    if (ldClient.isHost()) ldClient.endRoom();
  });
  document.getElementById("ldFreezeDotsBtn")?.addEventListener("click", () => {
    if (!ldClient.isHost()) return;
    hooks?.playModeCardBeep();
    const next = !(ldClient.room?.freezeDots ?? false);
    syncToggleSwitch(document.getElementById("ldFreezeDotsBtn"), next);
    ldClient.setFreezeDots(next);
  });
  document.getElementById("ldRainDotsBtn")?.addEventListener("click", () => {
    if (!ldClient.isHost()) return;
    hooks?.playModeCardBeep();
    rainDotsUiOpen = !rainDotsUiOpen;
    syncRainDotsUi();
  });
  document.getElementById("ldRainLaunchBtn")?.addEventListener("click", () => {
    if (!ldClient.isHost()) return;
    hooks?.playSelectSound();
    const balls = Math.max(1, Math.min(LD_RAIN_MAX_BALLS, Math.floor(Number(ldRainBallCountInput()?.value) || 0)));
    const perBall = Math.max(1, Math.floor(Number(ldRainAmountPerBallInput()?.value) || 0));
    if (ldRainBallCountInput()) ldRainBallCountInput()!.value = String(balls);
    if (ldRainAmountPerBallInput()) ldRainAmountPerBallInput()!.value = String(perBall);
    updateRainTotalCost();
    ldClient.rainLaunch(balls, perBall);
  });
  ldRainBallCountInput()?.addEventListener("input", () => updateRainTotalCost());
  ldRainAmountPerBallInput()?.addEventListener("input", () => updateRainTotalCost());

  document.getElementById("ldAddShopItemBtn")?.addEventListener("click", () => {
    if (!ldClient.isHost()) return;
    hooks?.playSelectSound();
    if (getSelectedLdShopItem()) {
      clearSelectedShopItem();
      return;
    }
    void openShopSelectScreen();
  });
  document.getElementById("ldShopSelectBackBtn")?.addEventListener("click", () => {
    closeShopSelectScreen();
  });
  document.getElementById("ldLeaderboardReturnBtn")?.addEventListener("click", () => {
    hooks?.playSelectSound();
    void handleLdReturn();
  });

  ldClient.onMessage((msg) => {
    if (msg.type === "error") {
      setListError(msg.message);
      setHostError(msg.message);
      setShopSelectError(msg.message);
    }
    if (msg.type === "rooms_list") {
      renderRoomsList(msg.rooms);
    }
    if (msg.type === "room_state") {
      if (msg.room.phase === "active") {
        hideLdScreens();
        closeShopSelectScreen(false);
        syncLootDropRemotesFromRoom();
        updateLdLeaderboard();
        showLdLeaderboardPanel();
        if (ldClient.isHost()) {
          showLdHostPanel();
          hideLdPlayerPanel();
          updateHostPanelBalance(msg.room.hostDotsBalance);
          updateLdSessionLimitUi(msg.room.sessionDotsSpent, msg.room.sessionDotsLimit);
          updateLdHostPlayersList();
          syncFreezeDotsToggle(msg.room.freezeDots);
          syncRainDotsUi();
          syncSelectedShopItemUi();
        } else {
          hideLdHostPanel();
          showLdPlayerPanel();
          updateLdPlayerPanelBalance(getDotsBalance());
          updateLdPlayerPanelHostBalance(msg.room.hostDotsBalance);
        }
        if (hooks && (!hooks.isPlaying() || !isLootDropActive())) {
          void startLootDropSession();
        }
      }
    }
    if (msg.type === "session_state") {
      if (ldClient.isHost()) {
        updateHostPanelBalance(msg.state.hostDotsBalance);
        updateLdSessionLimitUi(msg.state.sessionDotsSpent, msg.state.sessionDotsLimit);
        syncFreezeDotsToggle(msg.state.freezeDots);
      } else {
        updateLdPlayerPanelHostBalance(msg.state.hostDotsBalance);
      }
      updateLdLeaderboard();
      handleLdServerEvent(msg);
    }
    if (msg.type === "dots_updated") {
      const myId = hooks?.getAccountUserId?.();
      if (myId && msg.accountId === myId) {
        void persistLootDropDotsUpdate(msg.balance, msg.delta);
      }
    }
    if (msg.type === "item_awarded") {
      const myId = hooks?.getAccountUserId?.();
      if (myId && msg.accountId === myId) {
        void persistLootDropItemGrant(msg.cosmeticId, msg.cosmeticName);
      }
    }
    if (msg.type === "kicked") {
      void (async () => {
        await finalizeLootDropDots(msg.finalDotsBalance);
        stopLootDropSession();
        hideLdHostPanel();
        hideLdPlayerPanel();
        hideLdLeaderboardPanel();
        clearSelectedShopItem();
        rainDotsUiOpen = false;
        hooks?.stopPlaying();
        showListScreen();
        setListError(msg.reason || "You were kicked from this LootDrop");
        void refreshRoomsList();
      })();
    }
    if (msg.type === "session_ended") {
      void (async () => {
        const finalBal =
          typeof msg.finalDotsBalance === "number"
            ? msg.finalDotsBalance
            : ldLastRoomDotsBalance;
        await finalizeLootDropDots(finalBal);
        stopLootDropSession();
        hideLdHostPanel();
        hideLdPlayerPanel();
        hideLdLeaderboardPanel();
        clearSelectedShopItem();
        rainDotsUiOpen = false;
        hooks?.stopPlaying();
        showListScreen();
        setListError(msg.reason);
        void refreshRoomsList();
      })();
    }
  });
}

function setListError(msg: string): void {
  const el = ldListError();
  if (!el) return;
  if (msg) {
    el.textContent = msg;
    el.classList.remove("hidden");
  } else {
    el.textContent = "";
    el.classList.add("hidden");
  }
}

function setHostError(msg: string): void {
  const el = ldHostError();
  if (!el) return;
  if (msg) {
    el.textContent = msg;
    el.classList.remove("hidden");
  } else {
    el.textContent = "";
    el.classList.add("hidden");
  }
}

function setShopSelectError(msg: string): void {
  const el = document.getElementById("ldShopSelectError");
  if (!el) return;
  if (msg) {
    el.textContent = msg;
    el.classList.remove("hidden");
  } else {
    el.textContent = "";
    el.classList.add("hidden");
  }
}

function clearErrors(): void {
  setListError("");
  setHostError("");
  setShopSelectError("");
}

export function hideLdScreens(): void {
  hooks?.hideEl(ldListScreen());
  hooks?.hideEl(ldHostScreen());
  hooks?.hideEl(ldShopSelectScreen());
}

export function isLdScreenVisible(): boolean {
  const list = ldListScreen();
  const host = ldHostScreen();
  const shop = ldShopSelectScreen();
  return !!(
    (list && !list.classList.contains("hidden")) ||
    (host && !host.classList.contains("hidden")) ||
    (shop && !shop.classList.contains("hidden"))
  );
}

export async function openLootDropEntry(): Promise<void> {
  hooks?.hideEl(document.getElementById("modeScreen"));
  hideHubScreens();
  clearErrors();
  hooks?.showEl(ldListScreen());
  hooks?.showEl(document.getElementById("returnBtn"));
  hooks?.playScreenEnterMotion();
  hooks?.updateMusicState?.();
  await refreshRoomsList();
  startListPolling();
}

function startListPolling(): void {
  stopListPolling();
  listRefreshTimer = setInterval(() => {
    if (ldListScreen() && !ldListScreen()!.classList.contains("hidden")) {
      void refreshRoomsList();
    }
  }, 4000);
}

function stopListPolling(): void {
  if (listRefreshTimer) {
    clearInterval(listRefreshTimer);
    listRefreshTimer = null;
  }
}

async function refreshRoomsList(): Promise<void> {
  try {
    await ensureLdConnected();
    const accountId = hooks?.getAccountUserId?.() ?? null;
    let friendIds: string[] = [];
    if (accountId && hooks?.fetchFriends) {
      const friends = await hooks.fetchFriends();
      friendIds = friends.map((f) => f.id);
    }
    ldClient.listRooms(accountId, friendIds);
  } catch (err) {
    setListError(ldConnectionErrorMessage(err));
  }
}

function renderRoomsList(rooms: LdRoomListEntry[]): void {
  const ul = ldRoomsList();
  if (!ul) return;
  ul.replaceChildren();
  if (!rooms.length) {
    const li = document.createElement("li");
    li.className = "ldRoomItem ldRoomItem--empty";
    li.textContent = "No active LootDrops right now. Host one!";
    ul.appendChild(li);
    return;
  }
  for (const room of rooms) {
    const li = document.createElement("li");
    li.className = "ldRoomItem";
    const friendsTag = room.onlyFriends ? " · Only Friends" : "";
    const joinLabel = room.canJoin ? "Join" : room.onlyFriends ? "Friends only" : "Full";
    li.innerHTML = `
      <div class="ldRoomItemMain">
        <strong class="ldRoomHost">${escapeHtml(room.hostName)}</strong>
        <span class="ldRoomMeta">${room.playerCount} / ${room.maxCursors} players${friendsTag}</span>
      </div>
      <button type="button" class="btn ${room.canJoin ? "green" : ""} ldRoomJoinBtn" ${room.canJoin ? "" : "disabled"}>${joinLabel}</button>
    `;
    const btn = li.querySelector(".ldRoomJoinBtn") as HTMLButtonElement | null;
    btn?.addEventListener("click", () => void joinRoom(room.code));
    ul.appendChild(li);
  }
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

async function joinRoom(code: string): Promise<void> {
  clearErrors();
  hooks?.playSelectSound();
  const accountId = hooks?.getAccountUserId?.() ?? null;
  if (!accountId) {
    setListError("Sign in required to join a LootDrop");
    return;
  }
  try {
    await ensureLdConnected();
    const name = hooks?.getPlayerName() ?? "Player";
    if (
      !ldClient.joinRoom(
        code,
        name,
        accountId,
        resolveEquippedCosmeticIds(),
        getDotsBalance(),
        resolveCursorColor(),
      )
    ) {
      setListError("Not connected");
    }
  } catch (err) {
    setListError(ldConnectionErrorMessage(err));
  }
}

function showListScreen(): void {
  clearErrors();
  hideLdHostPanel();
  hideLdLeaderboardPanel();
  hooks?.hideEl(ldHostScreen());
  hooks?.hideEl(ldShopSelectScreen());
  hooks?.showEl(ldListScreen());
  hooks?.showEl(document.getElementById("returnBtn"));
  void refreshRoomsList();
}

function showHostScreen(): void {
  clearErrors();
  const signedIn = !!hooks?.getAccountUserId?.();
  if (!signedIn) {
    setListError("Sign in to host a LootDrop");
    return;
  }
  onlyFriendsToggle = false;
  syncOnlyFriendsToggle();
  const balEl = ldHostBalance();
  if (balEl) balEl.textContent = `${getDotsBalance().toLocaleString()} Dots`;
  const maxInput = ldMaxCursorsInput();
  if (maxInput) maxInput.value = "8";
  const limitInput = ldSessionDotsLimitInput();
  if (limitInput) limitInput.value = "";
  hooks?.hideEl(ldListScreen());
  hooks?.showEl(ldHostScreen());
  hooks?.showEl(document.getElementById("returnBtn"));
  hooks?.playScreenEnterMotion();
}

function syncOnlyFriendsToggle(): void {
  const btn = ldOnlyFriendsBtn();
  const hint = ldOnlyFriendsHint();
  const signedIn = !!hooks?.getAccountUserId?.();
  if (btn) {
    syncToggleSwitch(btn, onlyFriendsToggle && signedIn);
    btn.disabled = !signedIn;
  }
  if (hint) {
    hint.textContent = signedIn
      ? onlyFriendsToggle
        ? "Only your friends can join from the list."
        : "Anyone can join from the public list."
      : "Sign in to restrict joins to friends.";
  }
}

function toggleOnlyFriends(): void {
  if (!hooks?.getAccountUserId?.()) return;
  hooks?.playModeCardBeep();
  onlyFriendsToggle = !onlyFriendsToggle;
  syncOnlyFriendsToggle();
}

async function submitHost(): Promise<void> {
  if (hostInProgress) return;
  clearErrors();
  const accountId = hooks?.getAccountUserId?.();
  if (!accountId) {
    setHostError("Sign in required to host");
    return;
  }
  const maxCursors = Math.max(1, Math.min(32, Math.floor(Number(ldMaxCursorsInput()?.value) || 8)));
  const rawLimit = String(ldSessionDotsLimitInput()?.value ?? "").trim();
  const sessionDotsLimit = rawLimit
    ? Math.max(0, Math.min(100_000_000, Math.floor(Number(rawLimit) || 0)))
    : 0;
  hostInProgress = true;
  try {
    await ensureLdConnected();
    const name = hooks?.getPlayerName() ?? "Host";
    if (
      !ldClient.createRoom(
        name,
        accountId,
        maxCursors,
        onlyFriendsToggle,
        sessionDotsLimit,
        resolveEquippedCosmeticIds(),
        getDotsBalance(),
        resolveCursorColor(),
      )
    ) {
      setHostError("Not connected");
      return;
    }
    hooks?.hideEl(ldHostScreen());
    stopListPolling();
  } catch (err) {
    setHostError(ldConnectionErrorMessage(err));
  } finally {
    hostInProgress = false;
  }
}

function updateHostPanelBalance(balance: number): void {
  const el = document.getElementById("ldHostPanelBalance");
  if (el) el.textContent = `${Math.max(0, Math.floor(balance)).toLocaleString()} Dots`;
}

function ldRainBallCountInput(): HTMLInputElement | null {
  return document.getElementById("ldRainBallCountInput") as HTMLInputElement | null;
}

function ldRainAmountPerBallInput(): HTMLInputElement | null {
  return document.getElementById("ldRainAmountPerBallInput") as HTMLInputElement | null;
}

function syncFreezeDotsToggle(enabled: boolean): void {
  syncToggleSwitch(document.getElementById("ldFreezeDotsBtn"), enabled);
}

function syncRainDotsUi(): void {
  syncToggleSwitch(document.getElementById("ldRainDotsBtn"), rainDotsUiOpen);
  const fields = document.getElementById("ldRainDotsFields");
  if (fields) fields.classList.toggle("hidden", !rainDotsUiOpen);
  if (rainDotsUiOpen) updateRainTotalCost();
}

function updateRainTotalCost(): void {
  const balls = Math.max(0, Math.floor(Number(ldRainBallCountInput()?.value) || 0));
  const perBall = Math.max(0, Math.floor(Number(ldRainAmountPerBallInput()?.value) || 0));
  const total = balls * perBall;
  const el = document.getElementById("ldRainTotalCost");
  if (el) el.textContent = `Total: ${total.toLocaleString()} Dots`;
}

function syncSelectedShopItemUi(): void {
  const btn = document.getElementById("ldAddShopItemBtn");
  const hint = document.getElementById("ldSelectedShopItemHint");
  const selected = getSelectedLdShopItem();
  if (btn) btn.textContent = selected ? "Unselect" : "Add Shop Item";
  if (hint) {
    if (selected) {
      hint.textContent = `Selected: ${selected.name} (${selected.priceDots.toLocaleString()} Dots)`;
      hint.classList.remove("hidden");
    } else {
      hint.textContent = "";
      hint.classList.add("hidden");
    }
  }
  const amountInput = document.getElementById("ldDropAmountInput") as HTMLInputElement | null;
  if (amountInput) amountInput.disabled = !!selected;
}

function clearSelectedShopItem(): void {
  setSelectedLdShopItem(null);
  syncSelectedShopItemUi();
}

async function openShopSelectScreen(): Promise<void> {
  setShopSelectError("");
  const status = document.getElementById("ldShopSelectStatus");
  if (status) status.textContent = "Loading shop items…";
  const grid = document.getElementById("ldShopSelectGrid");
  if (grid) grid.replaceChildren();
  hooks?.hideEl(document.getElementById("ldHostPanel"));
  hooks?.showEl(ldShopSelectScreen());
  hooks?.showEl(document.getElementById("returnBtn"));
  try {
    const data = await fetchShopCatalog();
    // Only Dots-priced shop cosmetics — no free / achievement / challenge / score unlocks.
    shopSelectCatalog = (data.items || []).filter(
      (i) =>
        !i.crateOnly &&
        !i.sentOnly &&
        i.unlockType === "dots" &&
        Math.max(0, Math.floor(Number(i.priceDots) || 0)) > 0,
    );
    if (status) {
      status.textContent = shopSelectCatalog.length
        ? `${shopSelectCatalog.length} items`
        : "No paid shop items available.";
    }
    renderShopSelectGrid(shopSelectCatalog);
  } catch (e) {
    if (status) status.textContent = "";
    setShopSelectError(e instanceof Error ? e.message : "Failed to load shop");
  }
}

function closeShopSelectScreen(restoreHostPanel = true): void {
  hooks?.hideEl(ldShopSelectScreen());
  if (restoreHostPanel && isLootDropActive() && ldClient.isHost()) {
    showLdHostPanel();
  }
}

function renderShopSelectGrid(items: ShopCosmeticDto[]): void {
  const grid = document.getElementById("ldShopSelectGrid");
  if (!grid) return;
  grid.replaceChildren();
  for (const item of items) {
    const price = Math.max(0, Math.floor(Number(item.priceDots) || 0));
    if (price < 1) continue;
    const card = document.createElement("article");
    card.className = "shopCosmeticCard";
    const previewWrap = document.createElement("div");
    previewWrap.className = "shopCosmeticCardPreviewWrap";
    const img = document.createElement("img");
    img.className = "shopCosmeticCardPreview";
    img.alt = item.name;
    img.src = item.imageData || "";
    previewWrap.appendChild(img);
    card.appendChild(previewWrap);
    const name = document.createElement("h3");
    name.className = "shopCosmeticCardName";
    name.textContent = item.name;
    const priceEl = document.createElement("p");
    priceEl.className = "shopCosmeticCardPrice";
    priceEl.textContent = `${price.toLocaleString()} Dots`;
    card.appendChild(name);
    card.appendChild(priceEl);
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "btn green shopCosmeticCardBtn";
    btn.textContent = "Select";
    btn.onclick = () => {
      hooks?.playSelectSound();
      setSelectedLdShopItem({
        id: item.id,
        name: item.name,
        priceDots: price,
        imageData: item.imageData,
      });
      syncSelectedShopItemUi();
      closeShopSelectScreen(true);
    };
    card.appendChild(btn);
    void loadCosmeticImage(item.imageData).then((loaded) => {
      if (loaded) img.src = loaded.src;
    });
    grid.appendChild(card);
  }
}

/**
 * Apply room ledger update locally, then persist to the account API so Shop/Inventory stay correct.
 * The WS process only tracks in-room balances; /api/dots is the source of truth for accounts.
 *
 * Critical: never `applyDotsFromApi` on mid-session spend/credit responses — concurrent requests
 * race and overwrite local balance with a stale higher value while the host panel still shows
 * the correct room ledger. Persists are serialized; local UI always snaps to the WS balance.
 *
 * Mid-session persists use absolute `lootdrop-sync` (not delta spend/credit) so a missed or
 * reordered adjust cannot leave the account balance higher/lower than the room ledger.
 */
function persistLootDropDotsUpdate(balance: number, _deltaRaw?: number): void {
  const target = Math.max(0, Math.floor(balance));
  ldLastRoomDotsBalance = target;

  const current = getDotsBalance();
  const snapDelta = target - current;
  if (snapDelta !== 0) adjustDotsBalance(snapDelta);
  hooks?.refreshDotsUi?.();
  if (ldClient.isHost()) updateHostPanelBalance(target);
  else updateLdPlayerPanelBalance(target);

  ldDotsPersistChain = ldDotsPersistChain
    .catch(() => undefined)
    .then(async () => {
      // Always sync to the latest room ledger, not a stale target from when this was queued.
      const latest =
        ldLastRoomDotsBalance != null ? ldLastRoomDotsBalance : target;
      try {
        await postLootDropDotsSync(latest);
        if (ldLastRoomDotsBalance != null && getDotsBalance() !== ldLastRoomDotsBalance) {
          adjustDotsBalance(ldLastRoomDotsBalance - getDotsBalance());
          hooks?.refreshDotsUi?.();
        }
      } catch (e) {
        console.error("persistLootDropDotsUpdate", e);
      }
    });
}

/** Flush pending persists and snap account + local Dots to the final room ledger balance. */
async function finalizeLootDropDots(finalBalance?: number | null): Promise<void> {
  await ldDotsPersistChain.catch(() => undefined);
  const target =
    typeof finalBalance === "number" && Number.isFinite(finalBalance)
      ? Math.max(0, Math.floor(finalBalance))
      : ldLastRoomDotsBalance;
  if (target == null) return;

  if (getDotsBalance() !== target) {
    adjustDotsBalance(target - getDotsBalance());
  }
  hooks?.refreshDotsUi?.();
  if (ldClient.isHost()) updateHostPanelBalance(target);
  else updateLdPlayerPanelBalance(target);

  try {
    const res = await postLootDropDotsSync(target);
    if (res?.dots) {
      applyDotsFromApi(res.dots);
      // Ensure sync cannot leave us above/below the room final (API is authoritative after sync).
      if (getDotsBalance() !== target) {
        adjustDotsBalance(target - getDotsBalance());
      }
      hooks?.refreshDotsUi?.();
    }
  } catch (e) {
    console.error("finalizeLootDropDots", e);
  }

  ldLastRoomDotsBalance = null;
}

async function persistLootDropItemGrant(cosmeticId: string, _cosmeticName: string): Promise<void> {
  addOwnedCosmetic(cosmeticId);
  hooks?.refreshDotsUi?.();
  try {
    // Do not applyDotsFromApi here — it can clobber the room-synced Dots balance mid-session.
    await postLootDropGrantCosmetic(cosmeticId);
  } catch (e) {
    console.error("persistLootDropItemGrant", e);
  }
}

function leaveToHub(): void {
  withExitPlayTransition(() => {
    stopListPolling();
    stopLootDropSession();
    ldClient.leaveRoom();
    ldClient.disconnect();
    hideLdScreens();
    hideLdHostPanel();
    hideLdPlayerPanel();
    hideLdLeaderboardPanel();
    clearSelectedShopItem();
    hooks?.openGameHub();
  });
}

export function handleLdReturn(): boolean {
  if (ldShopSelectScreen() && !ldShopSelectScreen()!.classList.contains("hidden")) {
    closeShopSelectScreen(true);
    return true;
  }
  if (isLootDropActive()) {
    withExitPlayTransition(() => {
      const wasHost = ldClient.isHost();
      const finalBal = ldLastRoomDotsBalance;
      if (wasHost) {
        // Final reconcile happens on session_ended (includes uncollected drop refunds).
        ldClient.endRoom();
      } else {
        ldClient.leaveRoom();
        void finalizeLootDropDots(finalBal);
      }
      stopLootDropSession();
      hideLdHostPanel();
      hideLdPlayerPanel();
      hideLdLeaderboardPanel();
      clearSelectedShopItem();
      hooks?.releaseGamePointer?.();
      hooks?.stopPlaying();
      showListScreen();
    });
    return true;
  }
  if (isLdScreenVisible()) {
    if (ldHostScreen() && !ldHostScreen()!.classList.contains("hidden")) {
      withExitPlayTransition(() => {
        showListScreen();
      });
      return true;
    }
    leaveToHub();
    return true;
  }
  return false;
}

export { releaseLootDropPointer };

// Silence unused key warning (legacy guest name storage reserved).
void GUEST_NAME_KEY;
